/* eslint-disable kf/hook-func-name */
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  forwardRef,
  useMemo,
  useCallback,
  useImperativeHandle
} from "react";
import PropTypes from "prop-types";
import { Trans, t } from "@lingui/macro";
import { isEmpty, has } from "lodash";

import { generateId, debounce } from "base/utils";
import { getStore } from "base/basestore";
import { i18n } from "base/i18n";
import {
  Button,
  BUTTON_SIZES,
  SingleLine,
  SINGLELINE_SIZES
} from "widgets/inputs";
import { Icon } from "widgets/datadisplay";
import { Toast, Tooltip } from "widgets/feedback";
import { filePreviewAPI, usePreviewStore } from "kfplatform/filePreview";
import { FIXEDSIDE } from "base/constants";

import { Base as EditorBase } from "./editors/base";
import { Cell } from "./renderers/cell";
// import { ColumnHeader } from "./components/columheader";
import { DataSheetHeader } from "./components/header";
import { DataSheetFooter } from "./components/footer";
import { DataSheetEditor } from "./components/editors";
import { RowHeader } from "./components/rowheader";
import { DataSheetLoader } from "./components/loader";
import { usePrintStore } from "../formlayout/print/print.store";
import { useSignStore } from "./editors/signature/signature.store";
import { InfoIcon } from "../icons";
import { getRowHeight } from "./utils";
import { csvToJSON, jsonToCSV, stringToValue, valueToString } from "./parser";
import { RowDetail } from "./components/row_details";

import styles from "./datasheet.css";

import OpenIcon from "../icons/Expand.svg";

function findStartEnd(selectionStart, selectionEnd) {
  const corners = [
    selectionStart,
    selectionEnd,
    [selectionStart[0], selectionEnd[1]],
    [selectionEnd[0], selectionStart[1]]
  ];

  let end = [0, 0];
  let start = [Infinity, Infinity];

  for (let i = 0; i < corners.length; i++) {
    if (start[0] >= corners[i][0] && start[1] >= corners[i][1]) {
      start = corners[i];
    }
    if (end[0] <= corners[i][0] && end[1] <= corners[i][1]) {
      end = corners[i];
    }
  }
  return [start, end];
}

function convertRowToSourceFormat(row, rowIdentifier) {
  if (!row) return null;
  return Object.keys(row).reduce((memo, column) => {
    if (column === rowIdentifier) {
      memo[column] = row[column];
    } else {
      memo[column] = row[column] && row[column].value;
    }
    return memo;
  }, {});
}

// const parseClipboardGrid = (str) => {
//   let clipBoardRows = str.split(/\r\n|\n|\r/).map((row) => row.split("\t"));
//   const clipBoardRowsLength = clipBoardRows && clipBoardRows.length;
//   if (
//     clipBoardRowsLength > 1 &&
//     clipBoardRows[clipBoardRowsLength - 1].length === 1 &&
//     !clipBoardRows[clipBoardRowsLength - 1][0]
//   ) {
//     clipBoardRows.splice(clipBoardRowsLength - 1, 1);
//   }
//   return clipBoardRows;
// };

export function DataSheetComponent(
  {
    columns = [],
    rows = [],
    autoGrow = false,
    totalRowsCount = 100,
    showFooter = true,
    canAddColumn = true,
    canPinColumn = false,
    canAddRow = true,
    canSelectAll = true,
    maxRowsCount = 5000,
    updatePinnedColumns = () => {},
    onOffsetChange = () => {},
    onPageChange = () => {},
    onChange,
    onAddColumn,
    onResize,
    onReorder,
    onAddRow,
    onSelectAll,
    onSelectRow,
    rowIdentifier = "_id",
    id = "datasheet",
    addColumnPopup = null,
    footers = {},
    previewOption = false,
    togglePreview,
    previewRowData,
    isRowSharable = true,
    customProps = {},
    instanceId,
    isAddRowLoading = false,
    isSaved = false,
    showRowIndex = true,
    reRenderOnExternalChange = true,
    setIsFullScreen = () => {},
    isFullScreen,
    formConfig
  },
  ref
) {
  var printMode = usePrintStore((state) => {
    return state.printMode;
  });

  const SINGLE_ROW_HEIGHT = getRowHeight();

  const firstLoad = useRef(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOffsetChange = useCallback(
    debounce((offset, rowCount) => {
      onOffsetChange(offset);
      const page = Math.floor(offset / rowCount);
      onPageChange({
        offset: page * rowCount,
        page: page + 1,
        count: rowCount
      });
      if (
        offset !== page * rowCount &&
        (page + 1) * rowCount <= rowMatrix.current.length
      ) {
        onPageChange({
          offset: (page + 1) * rowCount,
          page: page + 2,
          count: rowCount
        });
      }
    }, 300),
    [onPageChange]
  );

  const [currentColumns, setCurrentColumns] = useState([
    ...columns.filter((column) => column.fixed),
    ...columns.filter((column) => !column.fixed)
  ]);

  const columnIds = useMemo(() => {
    return currentColumns.map((column) => column.id);
  }, [currentColumns]);

  const columnFiller = useMemo(
    () =>
      currentColumns.reduce((memo, column) => {
        memo[column.id] = { loading: true };
        return memo;
      }, {}),
    [currentColumns]
  );

  const fixedColumns = useMemo(
    () => currentColumns.filter((column) => column.fixed),
    [currentColumns]
  );

  const nonFixedColumns = useMemo(
    () => currentColumns.filter((column) => !column.fixed),
    [currentColumns]
  );

  const rowMatrix = useRef([]);

  // console.log("ROW MATRIC", rowMatrix);

  const totalWidth = "auto";

  const fixedColumnWidth =
    fixedColumns.reduce((memo, column) => {
      return memo + column.width;
    }, 0) +
    SINGLE_ROW_HEIGHT * (previewOption ? 2 : 1);

  const fixedColumnGridTemplate = `${SINGLE_ROW_HEIGHT}px ${
    previewOption ? `${SINGLE_ROW_HEIGHT}px` : ""
  } ${fixedColumns.map((column) => `${column.width}px`).join(" ")}`;

  const tableGridTemplate = printMode
    ? `${[...fixedColumns, ...nonFixedColumns]
        .map((column) => `${column.width}px`)
        .join(" ")} ${canAddColumn ? `${SINGLE_ROW_HEIGHT}px` : ""}`
    : `${fixedColumnWidth}px ${nonFixedColumns
        .map((column) => `${column.width}px`)
        .join(" ")} ${canAddColumn ? `${SINGLE_ROW_HEIGHT}px` : ""}`;

  const editorAbsoluteTop = useMemo(() => {
    return 0;
  }, []);

  const [windowedRows, setWindowedRows] = useState([{}]);

  const [rowCount, setRowCount] = useState(autoGrow ? totalRowsCount : 50);
  const [rowOffset, setRowOffset] = useState(0);

  const container = useRef(null);
  const sizer = useRef(null);
  const isSelecting = useRef(null);
  const scrollTop = useRef(null);
  const editingCell = useRef(null);
  const rowDetailRef = useRef(null);
  //
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const rowData = useRef({});
  // const [selectedCell, setSelectedCell] = useState([0, 0]);
  const [selectedCellStart, setSelectedCellStart] = useState(null);
  const [selectedCellEnd, setSelectedCellEnd] = useState(null);

  const calculateSelectedCellRange = useCallback(() => {
    const range = [];
    const cellStart = selectedCellStart;
    const cellEnd = selectedCellEnd || selectedCellStart;
    if (!cellStart) return [];
    if (!(cellStart[0] === cellEnd[0] && cellStart[1] === cellEnd[1])) {
      const [start, end] = findStartEnd(
        [columnIds.indexOf(cellStart[0]), cellStart[1]],
        [columnIds.indexOf(cellEnd[0]), cellEnd[1]]
      );

      for (let i = start[0]; i <= end[0]; i++) {
        for (let j = start[1]; j <= end[1]; j++) {
          range.push([columnIds[i], j]);
        }
      }
      return range;
    }
    return [cellStart];
  }, [columnIds, selectedCellEnd, selectedCellStart]);

  const selectedCellRange = useMemo(() => {
    return calculateSelectedCellRange();
  }, [calculateSelectedCellRange]);

  const onCellMouseDown = useCallback(
    (e) => {
      const { row, col, cell } = e.target.dataset;
      if (cell) {
        isSelecting.current = true;
        if (
          editingCell.current &&
          (editingCell.current.column.type === "Textarea" ||
            editingCell.current.column.type === "Currency")
        ) {
          rowMatrix.current[rowOffset + editingCell.current.rowIndex][
            editingCell.current.column.id
          ] = {
            value: editingCell.current.value
          };
          const currentRow =
            rowMatrix.current[rowOffset + editingCell.current.rowIndex];
          const convertedRow = convertRowToSourceFormat(
            currentRow,
            rowIdentifier
          );
          onChange(
            [
              {
                rowIndex: rowOffset + editingCell.current.rowIndex,
                column: editingCell.current.column,
                value: editingCell.current.value,
                row: convertedRow
              }
            ],
            rowMatrix.current
          );
          calculateWindowedRows(
            rowMatrix.current.slice(rowOffset, rowOffset + rowCount)
          );
          editingCell.current = null;
        }
        setTimeout(() => {
          setSelectedCellStart([
            col,
            parseInt(row, 10),
            windowedRows[parseInt(row - rowOffset, 10)][col]
          ]);
          setSelectedCellEnd([col, parseInt(row, 10)]);
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowedRows, rowOffset]
  );

  const onCellMouseUp = useCallback(
    (e) => {
      isSelecting.current = false;
      if (selectedCellRange.length > 1) {
        setSelectedRows([]);
      }
    },
    [selectedCellRange]
  );

  const onCellMouseMove = useCallback(
    (e) => {
      if (isSelecting.current) {
        const { row, col, cell } = e.target.dataset;
        if (cell) {
          if (
            !(
              selectedCellEnd &&
              selectedCellEnd[0] === col &&
              selectedCellEnd[1] === parseInt(row, 10)
            )
          ) {
            setSelectedCellEnd([col, parseInt(row, 10)]);
          }
        }
      }
    },
    [selectedCellEnd]
  );

  function setSizerSize(count) {
    if (sizer.current)
      sizer.current.style.height = `${count * SINGLE_ROW_HEIGHT + 320}px`;
  }

  setSizerSize(rowMatrix.current.length);

  const calculateWindowedRows = useCallback(
    (currentRows) => {
      currentRows.forEach((row) => {
        currentColumns.forEach((column) => {
          populateRow(row, column);
        });
      });
      setWindowedRows(currentRows);
      return currentRows;
    },
    [currentColumns]
  );

  const rerenderRows = useCallback(() => {
    rowMatrix.current = new Array(totalRowsCount).fill(columnFiller);
    if (rows.length > 0) {
      rows.forEach((row, i) => {
        const currentRow = {};
        columnIds.forEach((id) => {
          currentRow[id] = {
            value: has(row, id) ? row[id] : row.getValue && row.getValue(id),
            loading: false,
            errors: []
          };
        });
        currentRow[rowIdentifier] = row[rowIdentifier];
        if (rowIdentifier === "id") {
          currentRow.meta = row;
        }
        rowMatrix.current[i] = currentRow;
      });
      if (selectedCellStart && selectedCellStart[1] > totalRowsCount) {
        setSelectedCellStart(null);
        setSelectedCellEnd(null);
      } else if (
        selectedCellStart &&
        currentColumns.find((column) => column.id === selectedCellStart[0]) &&
        currentColumns.find((column) => column.id === selectedCellStart[0])
          .readOnly
      ) {
        setSelectedCellStart([
          selectedCellStart[0],
          selectedCellStart[1],
          rowMatrix.current[selectedCellStart[1]][selectedCellStart[0]]
        ]);
      }
    } else {
      rowMatrix.current = rows;
    }
    if (autoGrow || reRenderOnExternalChange) {
      setRowOffset(0);
      calculateHeight();
    }
    let tempRowsCount = rows.length;
    if (sizer.current && !autoGrow) {
      setSizerSize(rowMatrix.current.length);
      const height = container.current.clientHeight;
      tempRowsCount = Math.ceil(height / SINGLE_ROW_HEIGHT);
      setRowCount(tempRowsCount);
    } else {
      setRowCount(tempRowsCount);
    }
    if (!firstLoad.current) {
      setSelectedRows([]);
      onSelectRow && onSelectRow({ rows: [] });
    }
    calculateWindowedRows(rowMatrix.current.slice(0, tempRowsCount));

    firstLoad.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoGrow,
    calculateWindowedRows,
    columnFiller,
    rowCount,
    rowIdentifier,
    rows,
    totalRowsCount
  ]);

  let isFilePreviewOpen = usePreviewStore((state) => state.openPreview);

  useEffect(function filePreviewEffect() {
    return () => {
      if (isFilePreviewOpen) {
        filePreviewAPI.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(rerenderRows, [rows, autoGrow]);

  useEffect(
    function setRowData() {
      if (
        previewRowData &&
        previewRowData[rowIdentifier] !== rowData.current[rowIdentifier] &&
        windowedRows &&
        windowedRows.length > 0
      ) {
        setIsPreviewOpen(!isEmpty(previewRowData));
        if (!isEmpty(previewRowData)) {
          currentColumns.forEach((column) => {
            populateRow(previewRowData, column);
          });
          rowData.current = { ...previewRowData };
        } else {
          rowData.current = {};
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewRowData, windowedRows]
  );
  const rerenderColumns = useCallback(() => {
    if (!firstLoad.current) {
      rowMatrix.current.forEach((currentRow, rowIndex) => {
        columnIds.forEach((columnId) => {
          if (!currentRow[columnId]) {
            const row = rows[rowIndex] || {};
            currentRow[columnId] = {
              value: has(row, columnId)
                ? row[columnId]
                : (row?.getValue && row.getValue(columnId)) || null,
              loading: false,
              errors: []
            };
          }
        });
        for (let columnId in currentRow) {
          const system = ["_id", "Name", "id", "meta"];
          if (!system.includes(columnId) && !columnIds.includes(columnId)) {
            Reflect.deleteProperty(currentRow, columnId);
          }
        }
      });
      calculateWindowedRows(
        rowMatrix.current.slice(rowOffset, rowCount + rowOffset)
      );
      // setSelectedCellStart(null);
      // setSelectedCellEnd(null);
    }
  }, [calculateWindowedRows, columnIds, rowCount, rowOffset, rows]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(rerenderColumns, [columnIds]);

  useLayoutEffect(() => {
    if (autoGrow) {
      sizer.current.display = "none";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateHeight = useCallback(() => {
    if (autoGrow) {
      setTimeout(() => {
        const currentContainer = container.current;
        if (!currentContainer) return;
        const editingContainer = currentContainer.querySelector(".editing");
        const headerContainer = currentContainer.querySelector(".header");
        const tableContainer = currentContainer.querySelector(".table");
        let maxHeight =
          editingContainer &&
          editingContainer.offsetHeight +
            editingContainer.parentElement.offsetTop +
            headerContainer.offsetHeight +
            2;
        if (
          maxHeight <
          tableContainer.offsetHeight + headerContainer.offsetHeight
        ) {
          maxHeight =
            tableContainer.offsetHeight + headerContainer.offsetHeight;
        }
        if (currentContainer.scrollHeight > currentContainer.clientHeight) {
          currentContainer.style.height = `${
            currentContainer.scrollHeight + 11
          }px`;
        } else {
          currentContainer.style.height = `${maxHeight + 11}px`;
        }
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCellStart]);

  useLayoutEffect(() => {
    if (!autoGrow) {
      const scrollHandler = (e) => {
        if (scrollTop.current !== container.current.scrollTop) {
          if (rowOffset < rowMatrix.current.length) {
            const offset = Math.round(
              container.current.scrollTop / SINGLE_ROW_HEIGHT
            );
            if (editingCell.current) {
              editingCell.current.rowIndex =
                rowOffset - offset + editingCell.current.rowIndex;
            }
            if (offset >= rowMatrix.current.length) {
              setRowOffset(rowMatrix.current.length - 1);
              debouncedOffsetChange(rowMatrix.current.length - 1, rowCount);
            } else {
              setRowOffset(offset);
              debouncedOffsetChange(offset, rowCount);
            }
          }
          scrollTop.current = container.current.scrollTop;
        }
      };

      container.current.addEventListener("scroll", scrollHandler);

      const resizeHandler = () => {
        const height = container.current.clientHeight;
        setRowCount(Math.ceil(height / SINGLE_ROW_HEIGHT) - 2);
      };
      window.addEventListener("resize", resizeHandler);

      return () => {
        if (container.current) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          container.current.removeEventListener("scroll", scrollHandler);
        }
        window.removeEventListener("resize", resizeHandler);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOffsetChange, rowCount, rowOffset]);

  useEffect(() => {
    const fixedColumns = columns.filter((column) => column.fixed);
    const nonFixedColumns = columns.filter((column) => !column.fixed);
    setCurrentColumns([...fixedColumns, ...nonFixedColumns]);
  }, [columns]);

  const populateRow = (row, column) => {
    if (row[column.id]) {
      row[column.id].appearance =
        column.appearance &&
        column.appearance({
          row,
          value: row[column.id].value
        });
      row[column.id].isVisible = column.visibility
        ? column.visibility({
            row,
            value: row[column.id].value
          })
        : true;
      row[column.id].errors =
        row[column.id].isVisible && column.validator
          ? column.validator({
              row,
              value: row[column.id].value
            })
          : [];
      row[column.id].errors = row[column.id].errors || [];
    }
  };

  const [addRowCount, setAddRowCount] = useState(1);

  const addRow = useCallback(() => {
    onAddRow(
      {
        rowIndex: rowMatrix.current.length,
        lastRowId:
          rowMatrix.current[rowMatrix.current.length - 1] &&
          rowMatrix.current[rowMatrix.current.length - 1][rowIdentifier]
      },
      addRowCount
    );
  }, [onAddRow, addRowCount, rowIdentifier]);

  useEffect(() => {
    calculateWindowedRows(
      rowMatrix.current.slice(rowOffset, rowOffset + rowCount)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowOffset, rowCount]);

  useImperativeHandle(ref, () => ({
    appendRows: (offset, rows = []) => {
      if (rows.length > 0) {
        rows.forEach((row, i) => {
          const currentRow = {};
          columnIds.forEach((id) => {
            currentRow[id] = {
              value: has(row, id) ? row[id] : row.getValue && row.getValue(id),
              loading: false,
              errors: []
            };
          });
          currentRow[rowIdentifier] = row[rowIdentifier];
          if (rowIdentifier === "id") {
            currentRow.meta = row.meta;
          }
          let newRowMatrix = [...rowMatrix.current];
          newRowMatrix[offset + i] = currentRow;
          rowMatrix.current = newRowMatrix;
          if (
            isPreviewOpen &&
            rowData.current[rowIdentifier] === currentRow[rowIdentifier]
          ) {
            currentColumns.forEach((column) => {
              populateRow(currentRow, column);
            });
            rowData.current = { ...currentRow, rowIndex: offset };
          }
        });
      }
      if (autoGrow) {
        calculateWindowedRows(rowMatrix.current);
      } else {
        const windowedRows = rowMatrix.current.slice(
          rowOffset,
          rowOffset + rowCount
        );
        calculateWindowedRows(windowedRows);
        // if (selectedCellStart && selectedCellStart.length === 3) {
        //   setSelectedCellStart([
        //     selectedCellStart[0],
        //     selectedCellStart[1],
        //     windowedRows[selectedCellStart[1] - rowOffset][selectedCellStart[0]]
        //   ]);
        // }
      }
    },
    updateRow: (index, row) => {
      let rowIndex = index;
      // if (id) {
      //   rowIndex = rowMatrix.current.findIndex(
      //     (row) => row[rowIdentifier] === id
      //   );
      // }
      const currentRow = rowMatrix.current[rowIndex];
      columnIds.forEach((columnId) => {
        currentRow[columnId].value = row[columnId];
      });
      currentRow[rowIdentifier] = row[rowIdentifier];
      if (rowIdentifier === "id") {
        currentRow.meta = row;
      }
      calculateWindowedRows(
        rowMatrix.current.slice(rowOffset, rowOffset + rowCount)
      );
    },
    scrollToRight: () => {
      if (window.isRTL) {
        container.current.scrollLeft = 0;
      } else {
        container.current.scrollLeft =
          container.current.scrollWidth - container.current.clientWidth;
      }
    },
    scrollToLeft: () => {
      if (window.isRTL) {
        container.current.scrollLeft =
          -1 * (container.current.scrollWidth - container.current.clientWidth);
      } else {
        container.current.scrollLeft = 0;
      }
    },
    getAllRows: () => rowMatrix.current,
    deleteRows: (rowIds = []) => {
      rowIds.forEach((rowId) => {
        rowMatrix.current.splice(
          rowMatrix.current.findIndex((row) => row[rowIdentifier] === rowId),
          1
        );
      });
      if (rowMatrix.current.length < rowOffset) {
        calculateWindowedRows(rowMatrix.current);
        setRowOffset(0);
      } else {
        calculateWindowedRows(
          rowMatrix.current.slice(rowOffset, rowOffset + rowCount)
        );
      }

      setSelectedRows([]);
      setAllSelected(false);
    },
    focusOnError: () => {
      const invalidField =
        container.current.querySelector(`[data-valid="false"]`);
      if (invalidField) {
        invalidField.scrollIntoView();
      }
    },
    unSelectAll: () => setAllSelected(false),
    getRow: (rowId) => {
      let rowIndex = rowMatrix.current.findIndex(
        (row) => row[rowIdentifier] === rowId
      );
      if (rowIndex === -1) return null;
      return { ...rowMatrix.current[rowIndex], rowIndex };
    }
  }));
  const rowDetailCompleted = (value, column, index) => {
    let convertedRow, currentRow;
    if (
      index > -1 &&
      rowMatrix.current[index] &&
      rowMatrix.current[index][column.id]
    ) {
      rowMatrix.current[index][column.id].value = value;
      currentRow = rowMatrix.current[index];
      convertedRow = convertRowToSourceFormat(currentRow, rowIdentifier);
    } else if (!isEmpty(rowData.current)) {
      currentRow = rowData.current;
      convertedRow = convertRowToSourceFormat(currentRow, rowIdentifier);
    }
    onChange(
      [
        {
          rowIndex: index,
          column: column,
          value: value,
          row: convertedRow
        }
      ],
      rowMatrix.current
    );
    setTimeout(() => {
      currentColumns.forEach((column) => {
        populateRow(currentRow, column);
      });
      rowData.current = { ...currentRow, rowIndex: index };
    });
  };
  const editingCompleted = useCallback(() => {
    if (!editingCell.current) return;
    rowMatrix.current[rowOffset + editingCell.current.rowIndex][
      editingCell.current.column.id
    ] = {
      value: editingCell.current.value
    };
    const [col, row] = selectedCellStart;
    if (col === editingCell.current.column.id) {
      setSelectedCellStart([
        col,
        row,
        rowMatrix.current[rowOffset + editingCell.current.rowIndex][
          editingCell.current.column.id
        ]
      ]);
    }
    const currentRow =
      rowMatrix.current[rowOffset + editingCell.current.rowIndex];
    const convertedRow = convertRowToSourceFormat(currentRow, rowIdentifier);
    onChange(
      [
        {
          rowIndex: rowOffset + editingCell.current.rowIndex,
          column: editingCell.current.column,
          value: editingCell.current.value,
          row: convertedRow
        }
      ],
      rowMatrix.current
    );
    calculateWindowedRows(
      rowMatrix.current.slice(rowOffset, rowOffset + rowCount)
    );
    editingCell.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateWindowedRows, onChange, rowCount, rowOffset, selectedCellStart]);

  const editingNext = useCallback(() => {
    editingCompleted();
    setTimeout(() => {
      let newPos = [...selectedCellStart];
      const columnIndex = columnIds.findIndex((column) => column === newPos[0]);
      if (columnIndex < columnIds.length - 1) {
        newPos[0] = columnIds[columnIndex + 1];
      } else {
        newPos[0] = columnIds[0];
        newPos[1] = newPos[1] + 1;
      }
      if (rowMatrix.current[newPos[1]]) {
        newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
        setSelectedCellStart(newPos);
        setSelectedCellEnd(newPos);
      }
    });
  }, [columnIds, editingCompleted, selectedCellStart]);

  const editingPrevious = useCallback(() => {
    editingCompleted();
    setTimeout(() => {
      let newPos = [...selectedCellStart];
      const columnIndex = columnIds.findIndex((column) => column === newPos[0]);
      if (columnIndex > 0) {
        newPos[0] = columnIds[columnIndex - 1];
      } else {
        newPos[0] = columnIds[columnIds.length - 1];
        newPos[1] = newPos[1] - 1;
      }
      if (rowMatrix.current[newPos[1]]) {
        newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
        setSelectedCellStart(newPos);
        setSelectedCellEnd(newPos);
      }
    });
  }, [columnIds, editingCompleted, selectedCellStart]);
  const editingNextLine = useCallback(() => {
    editingCompleted();
    setTimeout(() => {
      let newPos = [...selectedCellStart];
      if (newPos[1] < rowMatrix.current.length - 1) {
        newPos[1] = newPos[1] + 1;
      }
      newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
      setSelectedCellStart(newPos);
      setSelectedCellEnd(newPos);
    });
  }, [editingCompleted, selectedCellStart]);

  const [allSelected, setAllSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const selectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        const currentRows = rowMatrix.current.map((row, i) => {
          return {
            rowIndex: i,
            [rowIdentifier]: row[rowIdentifier]
          };
        });
        setSelectedRows(currentRows);
        setAllSelected(true);
        onSelectAll && onSelectAll(true);
        onSelectRow && onSelectRow({ rows: currentRows });
      } else {
        setSelectedRows([]);
        setAllSelected(false);
        onSelectAll && onSelectAll(false);
        onSelectRow && onSelectRow({ rows: [] });
      }
      setSelectedCellEnd(selectedCellStart);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSelectAll, onSelectRow, selectedCellStart]
  );

  const selectRow = useCallback(
    (rowIndex, rowId) => {
      return (e) => {
        let currentSelectedRows;
        if (e.target.checked) {
          currentSelectedRows = [
            ...selectedRows,
            { rowIndex, [rowIdentifier]: rowId }
          ];
          setSelectedRows(currentSelectedRows);
          onSelectRow &&
            onSelectRow({
              rows: currentSelectedRows
            });
        } else {
          currentSelectedRows = selectedRows.filter(
            (row) => row.rowIndex !== rowIndex
          );
          setSelectedRows(currentSelectedRows);
          onSelectRow &&
            onSelectRow({
              rows: currentSelectedRows
            });
        }
        setAllSelected(currentSelectedRows.length === rowMatrix.current.length);
        setSelectedCellEnd(selectedCellStart);
      };
    },
    [onSelectRow, rowIdentifier, selectedCellStart, selectedRows]
  );

  const onContainerKeyDown = useCallback(
    (e) => {
      if (!selectedCellStart) return;
      let newPos = [...selectedCellStart];
      const navKeys = [
        "ArrowDown",
        "ArrowUp",
        "ArrowRight",
        "ArrowLeft",
        "Tab"
      ];
      if (navKeys.includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        editingCompleted();
        if (e.key === "ArrowDown") {
          if (e.ctrlKey || e.metaKey) {
            newPos[1] = rowMatrix.current.length - 1;
            newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
            container.current.scrollTop = container.current.scrollHeight;
          } else {
            newPos[1] += 1;
            if (rowMatrix.current[newPos[1]]) {
              newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
            }
            if (newPos[1] > rowOffset + rowCount - 3) {
              container.current.scrollTop += SINGLE_ROW_HEIGHT;
            }
          }
          if (rowMatrix.current[newPos[1]]) {
            if (!e.shiftKey) setSelectedCellEnd(newPos);
            setSelectedCellStart(newPos);
          }
        } else if (e.key === "ArrowUp") {
          if (e.ctrlKey || e.metaKey) {
            newPos[1] = 0;
            newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
            container.current.scrollTop = 0;
          } else {
            newPos[1] -= 1;
            if (rowMatrix.current[newPos[1]]) {
              newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
            }
            if (newPos[1] < rowOffset) {
              container.current.scrollTop -= SINGLE_ROW_HEIGHT;
            }
          }
          if (rowMatrix.current[newPos[1]]) {
            if (!e.shiftKey) setSelectedCellEnd(newPos);
            setSelectedCellStart(newPos);
          }
        } else if (e.key === (window.isRTL ? "ArrowRight" : "ArrowLeft")) {
          const columnIndex = columnIds.findIndex(
            (column) => column === newPos[0]
          );
          if (e.ctrlKey || e.metaKey) {
            newPos[0] = columnIds[0];
            newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
          } else if (columnIndex > 0) {
            newPos[0] = columnIds[columnIndex - 1];
            newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
          }

          if (!e.shiftKey) setSelectedCellEnd(newPos);
          setSelectedCellStart(newPos);
        } else if (e.key === (window.isRTL ? "ArrowLeft" : "ArrowRight")) {
          const columnIndex = columnIds.findIndex(
            (column) => column === newPos[0]
          );
          if (e.ctrlKey || e.metaKey) {
            newPos[0] = columnIds[columnIds.length - 1];
            newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
          } else if (columnIndex < columnIds.length - 1) {
            newPos[0] = columnIds[columnIndex + 1];
            newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
          }

          if (!e.shiftKey) setSelectedCellEnd(newPos);
          setSelectedCellStart(newPos);
        } else if (
          e.key === "Tab" &&
          (window.isRTL ? e.shiftKey : !e.shiftKey)
        ) {
          const columnIndex = columnIds.findIndex(
            (column) => column === newPos[0]
          );
          if (columnIndex < columnIds.length - 1) {
            newPos[0] = columnIds[columnIndex + 1];
          } else {
            newPos[0] = columnIds[0];
            newPos[1] = newPos[1] + 1;
          }
          if (newPos[1] > rowMatrix.current.length - 1) {
            return;
          }
          newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
          setSelectedCellStart(newPos);
          setSelectedCellEnd(newPos);
        } else if (
          e.key === "Tab" &&
          (window.isRTL ? !e.shiftKey : e.shiftKey)
        ) {
          const columnIndex = columnIds.findIndex(
            (column) => column === newPos[0]
          );
          if (columnIndex > 0) {
            newPos[0] = columnIds[columnIndex - 1];
          } else {
            newPos[0] = columnIds[columnIds.length - 1];
            newPos[1] = newPos[1] - 1;
          }
          newPos[2] = rowMatrix.current[newPos[1]][newPos[0]];
          setSelectedCellStart(newPos);
          setSelectedCellEnd(newPos);
        }
      } else if (e.key === "Delete" || e.key === "Backspace") {
        selectedCellRange.forEach((cell) => {
          var thisColumn = currentColumns.find(
            (column) => column.id === cell[0]
          );
          if (!thisColumn.readOnly && thisColumn.type !== "Checklist") {
            rowMatrix.current[cell[1]][cell[0]].value = null;
          }
          if (instanceId) {
            // eslint-disable-next-line no-unused-vars
            const useFormStore = getStore(instanceId);
            const flowData = useFormStore.getState().flowData;
            if (
              flowData.isPublicForm &&
              currentColumns.find((coulmn) => coulmn.type === "Signature")
            ) {
              let rowId = rowMatrix.current[cell[1]][rowIdentifier];
              let columnId = cell[0];
              useSignStore.getState().clearCellValue(rowId, columnId);
            }
          }
        });
        calculateWindowedRows(
          rowMatrix.current.slice(rowOffset, rowOffset + rowCount)
        );
        onChange(
          selectedCellRange
            .map((cell) => {
              if (
                currentColumns.find((column) => column.id === cell[0])
                  .readOnly ||
                currentColumns.find((column) => column.id === cell[0]).type ===
                  "Checklist"
              )
                return null;
              const convertedRow = convertRowToSourceFormat(
                rowMatrix.current[cell[1]],
                rowIdentifier
              );
              return {
                rowIndex: cell[1],
                column: currentColumns.find((column) => column.id === cell[0]),
                value: null,
                row: convertedRow
              };
            })
            .filter((change) => !!change),
          rowMatrix.current
        );
      } else if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        addRow();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      calculateWindowedRows,
      columnIds,
      currentColumns,
      onChange,
      rowCount,
      rowIdentifier,
      rowOffset,
      selectedCellRange,
      selectedCellStart
    ]
  );

  useLayoutEffect(() => {
    const copyHandler = (e) => {
      if (
        document.activeElement.closest(`#${id}`) ||
        document.activeElement === container.current
      ) {
        if (editingCell.current) {
          return;
        }
        e.preventDefault();
        const minRowIndex = Math.min(
          ...selectedCellRange.map((cell) => cell[1])
        );
        const minColumnIndex = Math.min(
          ...selectedCellRange.map((cell) => columnIds.indexOf(cell[0]))
        );
        const grid = [];
        selectedCellRange.forEach((cell) => {
          const column =
            currentColumns.find((column) => column.id === cell[0]) || {};
          grid[cell[1] - minRowIndex] = grid[cell[1] - minRowIndex] || [];
          grid[cell[1] - minRowIndex][
            columnIds.indexOf(cell[0]) - minColumnIndex
          ] = valueToString(
            rowMatrix.current[cell[1]][cell[0]].value,
            column.type,
            { currencies: column.CurrencyTypes }
          );
        });
        e.clipboardData.setData(
          "text/plain",
          grid.length === 1 && grid[0].length === 1 && grid[0][0]
            ? grid[0][0]
            : jsonToCSV(grid)
        );
      }
    };
    const pasteHandler = (e) => {
      if (
        document.activeElement.closest(`#${id}`) ||
        document.activeElement === container.current
      ) {
        if (editingCell.current) {
          return;
        }
        e.preventDefault();
        let text = (e.originalEvent || e).clipboardData.getData("text/plain");

        let grid = csvToJSON(text) || [];

        //excel linebreak removal
        if (
          grid.length > 1 &&
          grid[grid.length - 1].length === 1 &&
          grid[grid.length - 1][0] === ""
        ) {
          grid.splice(grid.length - 1, 1);
        }

        if (
          (grid.length === 1 && grid[0].length === 1 && grid[0][0]) ||
          (grid.length === 0 && text)
        ) {
          grid[0] = [text];
        }

        if (grid.length === 0) return;

        const minRowIndex = Math.min(
          ...selectedCellRange.map((cell) => cell[1])
        );
        const maxRowIndex = Math.max(
          ...selectedCellRange.map((cell) => cell[1])
        );
        const minColumnIndex = Math.min(
          ...selectedCellRange.map((cell) => columnIds.indexOf(cell[0]))
        );
        const changeSet = [];
        let noOfCellsAvailable = rowMatrix.current.length - minRowIndex;
        if (noOfCellsAvailable <= 0) return;
        if (!canAddRow) {
          grid = grid.slice(0, noOfCellsAvailable);
        }
        // To disallow user to paste rows if it exceeds the maximum rows limit.
        let availableRows = maxRowsCount - minRowIndex;
        if (availableRows === 0) return;
        if (availableRows > 0 && grid.length > availableRows) {
          Toast.showError({
            message: i18n._(
              t`Maximum row limit allocated for the dataset has exceeded`
            )
          });
          return;
        }
        // if (minRowIndex + grid.length > rowMatrix.current.length) {
        //   let diff = minRowIndex + grid.length - rowMatrix.current.length;
        //   onAddRow(
        //     {
        //       rowIndex: rowMatrix.current.length,
        //       lastRowId:
        //         rowMatrix.current[rowMatrix.current.length - 1] &&
        //         rowMatrix.current[rowMatrix.current.length - 1][rowIdentifier]
        //           .value
        //     },
        //     diff
        //   );
        // }

        setTimeout(() => {
          const selectedRowCount = maxRowIndex - minRowIndex + 1;
          let currentMinRowIndex = minRowIndex;
          for (let k = 0; k < Math.ceil(selectedRowCount / grid.length); k++) {
            currentMinRowIndex = minRowIndex + k * grid.length;
            let iterationCount = grid.length;
            if (selectedRowCount > grid.length) {
              iterationCount =
                k === Math.floor(selectedRowCount / grid.length)
                  ? selectedRowCount % grid.length
                  : grid.length;
            }
            if (selectedRowCount)
              for (let i = 0; i < iterationCount; i++) {
                let row = grid[i];
                let convertedRow = convertRowToSourceFormat(
                  rowMatrix.current[i + currentMinRowIndex],
                  rowIdentifier
                );
                for (let j = 0; j < row.length; j++) {
                  const column =
                    currentColumns.find(
                      (column) => column.id === columnIds[j + minColumnIndex]
                    ) || {};
                  if (column?.readOnly) {
                    continue;
                  }
                  let cellData = stringToValue(row[j], column.type, {
                    currencies: column.CurrencyTypes
                  });
                  if (
                    rowMatrix.current[i + currentMinRowIndex] &&
                    columnIds[j + minColumnIndex]
                  ) {
                    rowMatrix.current[i + currentMinRowIndex][
                      columnIds[j + minColumnIndex]
                    ].value = cellData;
                  }
                  if (!convertedRow) {
                    convertedRow = row.reduce((memo, cell, i) => {
                      memo[columnIds[i + minColumnIndex]] = cell;
                      return memo;
                    }, {});
                    currentColumns.reduce((_row, column, i) => {
                      if (!_row[column.Id] && column.DefaultValue) {
                        _row[column.Id] = stringToValue(
                          column.DefaultValue,
                          column.type,
                          {
                            currencies: column.CurrencyTypes
                          }
                        );
                      }
                      return _row;
                    }, convertedRow);
                  } else if (columnIds[j + minColumnIndex]) {
                    convertedRow[columnIds[j + minColumnIndex]] = cellData;
                  }
                  let isNewRow = false;
                  if (!convertedRow[rowIdentifier]) {
                    isNewRow = true;
                    convertedRow[rowIdentifier] = generateId(id);
                  }
                  let prevId =
                    rowMatrix.current[rowMatrix.current.length - 1] &&
                    rowMatrix.current[rowMatrix.current.length - 1][
                      rowIdentifier
                    ];
                  prevId = !prevId ? null : prevId;
                  if (!isEmpty(column)) {
                    changeSet.push({
                      rowIndex: i + currentMinRowIndex,
                      column,
                      value: cellData,
                      row: convertedRow,
                      isNew: isNewRow,
                      _previous_id: prevId
                    });
                  }
                }
              }
          }

          calculateWindowedRows(
            rowMatrix.current.slice(rowOffset, rowOffset + rowCount)
          );
          onChange(changeSet, rowMatrix.current);
        }, 100);
      }
    };
    document.addEventListener("copy", copyHandler);
    document.addEventListener("paste", pasteHandler);
    return () => {
      document.removeEventListener("copy", copyHandler);
      document.removeEventListener("paste", pasteHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnIds, selectedCellRange]);

  const onCellBlur = useCallback(
    function onCellBlurFunction() {
      editingCompleted();
      setSelectedCellStart(null);
      setSelectedCellEnd(null);
    },
    [editingCompleted]
  );

  useEffect(() => {
    const onBlur = (e) => {
      if (
        (container.current && !container.current.contains(e.target)) ||
        e.target.closest(".ant-select-dropdown")
      ) {
        onCellBlur();
      }
    };
    window.addEventListener("click", onBlur);
    return () => {
      window.removeEventListener("click", onBlur);
    };
  }, [editingCompleted, onCellBlur]);

  const onEditorStartEditing = useCallback((e) => {
    editingCell.current = {
      value: e.value,
      row: e.row,
      rowIndex: e.rowIndex,
      column: e.column
    };
    calculateHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEditorChange = useCallback((e) => {
    if (!editingCell.current) {
      editingCell.current = {
        value: e.value,
        row: e.row,
        rowIndex: e.rowIndex,
        column: e.column
      };
    }
    if (
      editingCell.current &&
      editingCell.current.rowIndex === e.rowIndex &&
      editingCell.current.column.id === e.column.id
    ) {
      editingCell.current.value = e.value;
    }
    calculateHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onHeaderResize = useCallback(
    (column, newWidth) => {
      const columns = currentColumns.slice(0);
      columns.find((col) => col.id === column.id).width = newWidth;
      onCellBlur();
      setCurrentColumns(columns);
      onResize && onResize(columns, column, newWidth);
    },
    [currentColumns, onCellBlur, onResize]
  );

  const onHeaderReorder = useCallback(
    (currentColumn, nextToColumn) => {
      let columns = currentColumns.slice(0);
      let currentColumnIndex = columns.findIndex(
        (col) => col.id === currentColumn.id
      );
      let nextToColumnIndex =
        nextToColumn && columns.findIndex((col) => col.id === nextToColumn.id);
      if (!nextToColumn) {
        columns = columns.filter(
          (value, index) => index !== currentColumnIndex
        );
        currentColumn.fixed = FIXEDSIDE.LEFT;
        columns.unshift(currentColumn);
      } else if (currentColumnIndex !== nextToColumnIndex + 1) {
        columns[currentColumnIndex].fixed =
          nextToColumn.fixed && columns[nextToColumnIndex + 1]?.fixed
            ? FIXEDSIDE.LEFT
            : false;

        if (nextToColumnIndex > currentColumnIndex) {
          columns.splice(
            nextToColumnIndex,
            0,
            columns.splice(currentColumnIndex, 1)[0]
          );
        } else {
          columns.splice(
            nextToColumnIndex + 1,
            0,
            columns.splice(currentColumnIndex, 1)[0]
          );
        }
      }
      onCellBlur();
      setCurrentColumns(columns);
      onReorder && onReorder(columns, currentColumn, nextToColumn);
    },
    [currentColumns, onCellBlur, onReorder]
  );

  const onPinColumn = (column, pinColumn) => {
    let modifiedColumns = handlePinColumns(currentColumns, column);
    setCurrentColumns(modifiedColumns);
    let pinnedColumns = [];
    modifiedColumns.forEach((col) => {
      if (col.fixed) {
        pinnedColumns.push(col.Id);
      }
    });
    updatePinnedColumns(pinnedColumns);
  };

  function handlePinColumns(columns, column) {
    let columnIndex = currentColumns.findIndex((col) => col.id === column.id);
    columns[columnIndex]["fixed"] = column.fixed ? null : FIXEDSIDE.LEFT;
    let pinnedColumns = [];
    let unPinnedColumns = [];
    columns.forEach((column) => {
      if (column.fixed) {
        pinnedColumns.push(column);
      } else {
        unPinnedColumns.push(column);
      }
    });
    return [...pinnedColumns, ...unPinnedColumns];
  }

  return (
    <div
      ref={container}
      className={`${styles.container} ${autoGrow ? styles.autoGrow : ""} ${
        canPinColumn && fixedColumns.length > 0 ? styles.activeFixed : ""
      } dataSheetContainer`}
      onKeyDown={onContainerKeyDown}
      tabIndex={0}
      id={id}
      // onClick={(e) => {
      //   container.current.focus();
      // }}
    >
      <div ref={sizer} className={styles.sizer}>
        &nbsp;
      </div>
      <div className={styles.innercontainer}>
        <DataSheetHeader
          totalWidth={totalWidth}
          tableGridTemplate={tableGridTemplate}
          fixedColumnWidth={fixedColumnWidth}
          fixedColumnGridTemplate={fixedColumnGridTemplate}
          fixedColumns={fixedColumns}
          nonFixedColumns={nonFixedColumns}
          onAddColumn={onAddColumn}
          canAddColumn={canAddColumn}
          canSelectAll={canSelectAll}
          addColumnPopup={addColumnPopup}
          onResize={onHeaderResize}
          onReorder={onHeaderReorder}
          allSelected={allSelected}
          onSelectAll={selectAll}
          previewOption={previewOption}
          handlePinColumn={onPinColumn}
          rootContainer={container.current}
        />
        <div
          className={`${styles.table} table`}
          style={{
            gridTemplateColumns: tableGridTemplate,
            width: `${totalWidth}px`
          }}
          onMouseDown={onCellMouseDown}
          onMouseMove={onCellMouseMove}
          onMouseUp={onCellMouseUp}
        >
          {windowedRows.length === 0 && (
            <div className={styles.empty}>
              <div>No data found</div>
            </div>
          )}
          {windowedRows.map((row, rowIndex) => {
            return (
              <React.Fragment key={`row_${rowIndex}`}>
                <div
                  className={`${styles.leftFixed}`}
                  style={{
                    gridTemplateColumns: fixedColumnGridTemplate,
                    width: `${fixedColumnWidth}px`
                  }}
                >
                  <RowHeader
                    isRowSelect={true}
                    label={(rowOffset + rowIndex + 1).toString()}
                    canSelect={canSelectAll}
                    checked={
                      !!selectedRows.find(
                        (row) => row.rowIndex === rowOffset + rowIndex
                      )
                    }
                    onChange={selectRow(
                      rowOffset + rowIndex,
                      rowMatrix.current[rowOffset + rowIndex] &&
                        rowMatrix.current[rowOffset + rowIndex][rowIdentifier]
                    )}
                  />
                  {previewOption && (
                    <div className={styles.previewBtn}>
                      <Tooltip
                        content={i18n._(t`Expand row`)}
                        key={`tip-${rowIndex}`}
                        position="top"
                      >
                        <Icon
                          onClick={() =>
                            togglePreview(true, row[rowIdentifier])
                          }
                          className={styles.previewIcon}
                        >
                          <OpenIcon />
                        </Icon>
                      </Tooltip>
                    </div>
                  )}

                  {fixedColumns.map((column, colIndex) => {
                    const cell = row[column.id];
                    return (
                      cell && (
                        <Cell
                          key={`row_${rowIndex}_${colIndex}`}
                          column={column}
                          value={cell.value}
                          rowIndex={rowOffset + rowIndex}
                          row={convertRowToSourceFormat(row, rowIdentifier)}
                          isFixed={true}
                          errors={cell.errors}
                          appearance={cell.appearance}
                          isLoading={cell.loading}
                          isVisible={cell.isVisible}
                          isSelected={
                            !!selectedCellRange.find(
                              (cell) =>
                                cell[0] === column.id &&
                                cell[1] === rowOffset + rowIndex
                            ) ||
                            !!selectedRows.find(
                              (row) => row.rowIndex === rowOffset + rowIndex
                            )
                          }
                          instanceId={instanceId}
                        />
                      )
                    );
                  })}
                </div>
                {printMode &&
                  fixedColumns.map((column, colIndex) => {
                    const cell = row[column.id];
                    return (
                      cell && (
                        <Cell
                          key={`row_${rowIndex}_${colIndex}`}
                          column={column}
                          value={cell.value}
                          rowIndex={rowOffset + rowIndex}
                          row={convertRowToSourceFormat(row, rowIdentifier)}
                          isFixed={true}
                          errors={cell.errors}
                          appearance={cell.appearance}
                          isLoading={cell.loading}
                          isVisible={cell.isVisible}
                          isSelected={
                            !!selectedCellRange.find(
                              (cell) =>
                                cell[0] === column.id &&
                                cell[1] === rowOffset + rowIndex
                            ) ||
                            !!selectedRows.find(
                              (row) => row.rowIndex === rowOffset + rowIndex
                            )
                          }
                          instanceId={instanceId}
                        />
                      )
                    );
                  })}
                {nonFixedColumns &&
                  nonFixedColumns.map((column, colIndex) => {
                    const cell = row[column.id];
                    return (
                      cell && (
                        <Cell
                          key={`row_${rowIndex}_${colIndex}`}
                          column={column}
                          value={cell.value}
                          rowIndex={rowOffset + rowIndex}
                          row={convertRowToSourceFormat(row, rowIdentifier)}
                          isFixed={true}
                          errors={cell.errors}
                          appearance={cell.appearance}
                          isLoading={cell.loading}
                          isVisible={cell.isVisible}
                          isSelected={
                            !!selectedCellRange.find(
                              (cell) =>
                                cell[0] === column.id &&
                                cell[1] === rowOffset + rowIndex
                            ) ||
                            !!selectedRows.find(
                              (row) => row.rowIndex === rowOffset + rowIndex
                            )
                          }
                          instanceId={instanceId}
                        />
                      )
                    );
                  })}
                {canAddColumn && <div className={`${styles.rightFixed}`} />}

                {/* <div className={styles.emptyspace} /> */}
              </React.Fragment>
            );
          })}
          {canAddRow && (
            <React.Fragment key={`AddRow`}>
              <div className={styles.addRow}>
                <div>
                  <div>
                    <Button
                      size={BUTTON_SIZES.TINY}
                      disabled={
                        !addRowCount || maxRowsCount <= rowMatrix.current.length
                      }
                      onClick={addRow}
                      isLoading={isAddRowLoading}
                    >
                      <Trans>Add</Trans>
                    </Button>
                  </div>
                  <div>
                    {maxRowsCount <= rowMatrix.current.length ? (
                      <div className={styles.errorMessage}>
                        <Icon className={styles.infoIcon}>
                          <InfoIcon />
                        </Icon>
                        <span className={"small errorMessage"}>
                          <Trans>Maximum row limit reached</Trans>
                        </span>
                      </div>
                    ) : (
                      <>
                        <SingleLine
                          size={SINGLELINE_SIZES.TINY}
                          value={addRowCount}
                          onChange={(value) => {
                            setAddRowCount(Number(value));
                          }}
                          onFocus={onCellBlur}
                        />
                        <span className={"small"}>
                          <Trans>more rows</Trans>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {/* <div
                className={`${styles.leftFixed}`}
                style={{
                  gridTemplateColumns: fixedColumnGridTemplate,
                  width: `${fixedColumnWidth}px`
                }}
              >
                <div className={styles.rowAddButton}>
                  <button
                    className={`${styles.addButton} shadow-12dp`}
                    onClick={addRow}
                  />
                </div>
                {fixedColumns.map((column, colIndex) => {
                  return (
                    <div
                      className={`${styles.cell} ${styles.addCell}`}
                      key={column.id}
                      onClick={addRow}
                    />
                  );
                })}
              </div>
              {nonFixedColumns.map((column, colIndex) => {
                return (
                  <div
                    className={`${styles.cell} ${styles.addCell}`}
                    key={column.id}
                    onClick={addRow}
                  />
                );
              })}
              {canAddColumn && <div className={`${styles.rightFixed}`} />} */}
            </React.Fragment>
          )}
          <DataSheetEditor
            absoluteTop={editorAbsoluteTop}
            columns={currentColumns}
            cell={selectedCellStart}
            rowIndex={selectedCellStart && selectedCellStart[1] - rowOffset}
            row={
              selectedCellStart &&
              convertRowToSourceFormat(
                rowMatrix.current[selectedCellStart[1]],
                rowIdentifier
              )
            }
            previewOption={previewOption}
            totalWidth={totalWidth}
            tableGridTemplate={tableGridTemplate}
            fixedColumnWidth={fixedColumnWidth}
            fixedColumnGridTemplate={fixedColumnGridTemplate}
            onStartEditing={onEditorStartEditing}
            onChange={onEditorChange}
            onCompleted={editingCompleted}
            onNext={editingNext}
            onNextLine={editingNextLine}
            onPrevious={editingPrevious}
            container={container.current}
            instanceId={instanceId}
            customProps={customProps}
            setIsFullScreen={setIsFullScreen}
            isFullScreen={isFullScreen}
            formConfig={formConfig}
          />
        </div>

        {showFooter && (
          <DataSheetFooter
            totalWidth={totalWidth}
            tableGridTemplate={tableGridTemplate}
            fixedColumnWidth={fixedColumnWidth}
            fixedColumnGridTemplate={fixedColumnGridTemplate}
            fixedColumns={fixedColumns}
            nonFixedColumns={nonFixedColumns}
            canAddColumn={canAddColumn}
            footers={footers}
            previewOption={previewOption}
          />
        )}
        {isPreviewOpen && (
          <RowDetail
            isOpen={true}
            shareOption={isRowSharable}
            showRowIndex={showRowIndex}
            onClose={() => togglePreview(false, null)}
            row={rowData.current}
            titleText={rowData.current?.Name?.value || ""}
            columns={currentColumns}
            onCompleted={rowDetailCompleted}
            innerRef={rowDetailRef}
            isSaved={isSaved}
            instanceId={instanceId}
            rowIdentifier={rowIdentifier}
          />
        )}
      </div>
    </div>
  );
}

DataSheetComponent.propTypes = {
  columns: PropTypes.array,
  rows: PropTypes.array,
  footers: PropTypes.any,
  totalRowsCount: PropTypes.number,
  showFooter: PropTypes.bool,
  onOffsetChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onChange: PropTypes.func,
  onAddColumn: PropTypes.func,
  canAddColumn: PropTypes.bool,
  canAddRow: PropTypes.bool,
  canPinColumn: PropTypes.bool,
  canSelectAll: PropTypes.bool,
  rowIdentifier: PropTypes.string,
  autoGrow: PropTypes.bool,
  onResize: PropTypes.func,
  onReorder: PropTypes.func,
  onAddRow: PropTypes.func,
  onSelectAll: PropTypes.func,
  onSelectRow: PropTypes.func,
  addColumnPopup: PropTypes.func,
  id: PropTypes.string,
  previewRowData: PropTypes.any,
  isRowSharable: PropTypes.bool,
  customProps: PropTypes.object,
  maxRowsCount: PropTypes.number,
  previewOption: PropTypes.bool,
  togglePreview: PropTypes.func,
  instanceId: PropTypes.string,
  isAddRowLoading: PropTypes.bool,
  reRenderOnExternalChange: PropTypes.bool,
  isSaved: PropTypes.bool,
  showRowIndex: PropTypes.bool,
  setIsFullScreen: PropTypes.func,
  isFullScreen: PropTypes.bool,
  updatePinnedColumns: PropTypes.func,
  formConfig: PropTypes.object
};

export const DataSheet = forwardRef(DataSheetComponent);

export { DataSheetLoader };

export { EditorBase };
